import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Home, Download, RefreshCw, Search, XCircle, ListChecks, ChevronLeft, FileText,
} from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StandardLayout from '../components/common/StandardLayout';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import LeadStatusPill from '../components/admin/LeadStatusPill';
import LeadNotesPopover from '../components/admin/LeadNotesPopover';
import promotionsService from '../services/promotionsService';
import { LEAD_STATUSES, Lead, LeadStatus, Promotion } from '../types/promotion';
import { useAuth } from '../context/AuthContext';

const PromotionLeadsPage: React.FC = () => {
  const { promoId = '' } = useParams<{ promoId: string }>();
  const { isAdmin } = useAuth();
  const [promo, setPromo] = useState<Promotion | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<LeadStatus | ''>('');
  const [search, setSearch] = useState('');
  const [editingNotes, setEditingNotes] = useState<Lead | null>(null);
  const [exporting, setExporting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [promoData, leadsResult] = await Promise.all([
        promotionsService.getPromotion(promoId),
        promotionsService.listLeads(promoId, {
          status: statusFilter || undefined,
          search: search.trim() || undefined,
        }),
      ]);
      setPromo(promoData);
      setLeads(leadsResult.leads);
      setCursor(leadsResult.cursor);
    } catch (err: any) {
      setError(err?.message || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [promoId, statusFilter, search]);

  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const next = await promotionsService.listLeads(promoId, {
        status: statusFilter || undefined,
        search: search.trim() || undefined,
        cursor,
      });
      setLeads((prev) => [...prev, ...next.leads]);
      setCursor(next.cursor);
    } catch (err: any) {
      setError(err?.message || 'Failed to load more leads');
    } finally {
      setLoadingMore(false);
    }
  }, [promoId, statusFilter, search, cursor, loadingMore]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleStatusChange = async (lead: Lead, status: LeadStatus) => {
    const prev = lead.status;
    setLeads((cur) => cur.map((l) => (l.sk === lead.sk ? { ...l, status } : l)));
    try {
      const updated = await promotionsService.updateLead(promoId, lead.sk, { status });
      setLeads((cur) => cur.map((l) => (l.sk === updated.sk ? { ...l, ...updated } : l)));
    } catch (err: any) {
      // Rollback
      setLeads((cur) => cur.map((l) => (l.sk === lead.sk ? { ...l, status: prev } : l)));
      alert(err?.message || 'Failed to update status');
    }
  };

  const handleSaveNotes = async (lead: Lead, notes: string) => {
    const updated = await promotionsService.updateLead(promoId, lead.sk, { notes });
    setLeads((cur) => cur.map((l) => (l.sk === updated.sk ? { ...l, ...updated } : l)));
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await promotionsService.exportLeadsCsv(promoId);
    } catch (err: any) {
      alert(err?.message || 'Failed to export');
    } finally {
      setExporting(false);
    }
  };

  // Summary cards
  const counts = useMemo(() => {
    const c: Record<LeadStatus | 'TOTAL', number> = {
      TOTAL: leads.length, NEW: 0, CONTACTED: 0, QUALIFIED: 0, CONVERTED: 0, DROPPED: 0,
    };
    for (const l of leads) c[l.status] = (c[l.status] || 0) + 1;
    return c;
  }, [leads]);

  return (
    <StandardLayout>
      <PageHeader
        title={promo?.title || 'Leads'}
        pageType="management"
        icon={<ListChecks className="w-5 h-5" />}
        breadcrumbs={[
          { label: 'Home', href: '/', icon: <Home className="w-4 h-4" /> },
          { label: 'Platform' },
          { label: 'Listings' },
          { label: 'Promotions', href: '/admin/promotions' },
        ]}
        actions={
          <div className="flex flex-wrap gap-2">
            <Link to="/admin/promotions">
              <Button variant="ghost" leftIcon={<ChevronLeft className="h-4 w-4" />} className="w-full sm:w-auto">
                Back
              </Button>
            </Link>
            <Button
              variant="primary"
              onClick={handleExport}
              leftIcon={<Download className="h-4 w-4" />}
              isLoading={exporting}
              className="w-full sm:w-auto"
            >
              Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={fetchAll}
              leftIcon={<RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              Refresh
            </Button>
          </div>
        }
      />

      {!isAdmin && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
          <XCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-red-800 dark:text-red-200 font-medium">Access Denied</p>
          <p className="text-red-600 dark:text-red-400 text-sm mt-1">This page is only accessible to administrators.</p>
        </div>
      )}

      {isAdmin && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { label: 'Total', value: counts.TOTAL, color: 'text-gray-900 dark:text-white' },
              { label: 'New', value: counts.NEW, color: 'text-blue-600 dark:text-blue-400' },
              { label: 'Contacted', value: counts.CONTACTED, color: 'text-amber-600 dark:text-amber-400' },
              { label: 'Qualified', value: counts.QUALIFIED, color: 'text-purple-600 dark:text-purple-400' },
              { label: 'Converted', value: counts.CONVERTED, color: 'text-emerald-600 dark:text-emerald-400' },
              { label: 'Dropped', value: counts.DROPPED, color: 'text-gray-500 dark:text-gray-400' },
            ].map((c) => (
              <div key={c.label} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{c.label}</div>
                <div className={`text-xl font-bold mt-1 tabular-nums ${c.color}`}>{c.value}</div>
              </div>
            ))}
          </div>

          <Card>
            <CardHeader>
              <CardTitle>
                Leads ({leads.length}
                {typeof promo?.lead_count === 'number' && promo.lead_count !== leads.length
                  ? ` of ${promo.lead_count}`
                  : ''}
                )
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {/* Filter bar */}
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as LeadStatus | '')}
                    className="h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 px-3 focus:outline-none focus:ring-2 focus:ring-zebu-500"
                  >
                    <option value="">All statuses</option>
                    {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <div className="flex-1 min-w-[180px] max-w-md">
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') fetchAll(); }}
                      leftIcon={<Search className="h-4 w-4" />}
                      placeholder="Search name / email / phone…"
                    />
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchAll}>Apply</Button>
                </div>
              </div>

              {loading && (
                <div className="px-6 py-8">
                  <LoadingSpinner message="Loading leads..." />
                </div>
              )}
              {error && (
                <div className="px-6 py-4">
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-red-600" />
                    <span className="text-red-800 dark:text-red-200">{error}</span>
                  </div>
                </div>
              )}
              {!loading && !error && leads.length === 0 && (
                <div className="text-center py-12 px-6">
                  <ListChecks className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No leads yet</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Leads will appear here as visitors submit the form.
                  </p>
                </div>
              )}
              {!loading && !error && leads.length > 0 && (
                <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Captured</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Contact</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Source</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {leads.map((lead) => {
                        const data = lead.data || {};
                        const name = data.name || '—';
                        const email = data.email || '';
                        const phone = data.phone || '';
                        const created = lead.created_at ? new Date(lead.created_at).toLocaleString() : '—';
                        return (
                          <tr key={lead.sk} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 text-xs text-gray-600 dark:text-gray-400 tabular-nums whitespace-nowrap">{created}</td>
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">{name}</div>
                              {email && <div className="text-xs text-gray-500 dark:text-gray-400">{email}</div>}
                              {phone && <div className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">{phone}</div>}
                              {lead.basket_id && (
                                <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-1 font-mono">basket: {lead.basket_id}</div>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                {lead.source || '—'}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <select
                                value={lead.status}
                                onChange={(e) => handleStatusChange(lead, e.target.value as LeadStatus)}
                                className="h-8 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-xs text-gray-900 dark:text-gray-100 px-2 focus:outline-none focus:ring-2 focus:ring-zebu-500"
                              >
                                {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                              </select>
                              <div className="mt-1">
                                <LeadStatusPill status={lead.status} />
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <button
                                type="button"
                                onClick={() => setEditingNotes(lead)}
                                className="inline-flex items-center gap-1 text-sm text-zebu-600 dark:text-zebu-400 hover:underline"
                              >
                                <FileText className="h-4 w-4" />
                                {lead.notes ? `${lead.notes.slice(0, 60)}${lead.notes.length > 60 ? '…' : ''}` : 'Add notes'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {cursor && (
                  <div className="flex items-center justify-center px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                    <Button
                      variant="outline"
                      onClick={loadMore}
                      isLoading={loadingMore}
                      disabled={loadingMore}
                    >
                      Load more
                    </Button>
                  </div>
                )}
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {editingNotes && (
        <LeadNotesPopover
          open={!!editingNotes}
          initialNotes={editingNotes.notes || ''}
          onClose={() => setEditingNotes(null)}
          onSave={async (notes) => { await handleSaveNotes(editingNotes, notes); }}
        />
      )}
    </StandardLayout>
  );
};

export default PromotionLeadsPage;
